<script setup lang="ts">
import { cn } from '@/utils';
import { useVModel } from '@vueuse/core';
import { inputContainerVariants, type InputProps, inputVariants } from '.';

const props = withDefaults(defineProps<InputProps>(), {
	variant: 'default',
	unitPosition: 'right',
	autofocus: false,
	cyTestId: ''
});

const emits = defineEmits<{
	(e: 'update:modelValue', payload: string | number): void;
	(e: 'update:isFocused', payload: boolean): void;
}>();

const modelValue = useVModel(props, 'modelValue', emits, {
	passive: true,
	defaultValue: props.defaultValue
});

const inputRef = useTemplateRef<HTMLInputElement>('inputRef');

const { focused } = useFocus(inputRef);

watch(focused, newValue => {
	emits('update:isFocused', newValue);
});

whenever(
	() => props.autofocus,
	async () => {
		if (!props.autofocus) return;

		await nextTick();

		if (!inputRef.value) return;

		inputRef.value.focus();
	},
	{ immediate: true }
);

defineExpose({ focused });
</script>

<template>
	<div :class="inputContainerVariants({ variant })">
		<input
			ref="inputRef"
			v-model="modelValue"
			:data-test-id="cyTestId"
			:class="[
				cn(inputVariants({ variant: props.variant }), props.class),
				{ '!border-error': props.error }
			]"
			v-bind="$attrs"
			:placeholder="props.variant === 'insideLabel' ? '' : props.placeholder"
		/>
		<slot name="affix" />
		<label
			v-if="props.variant === 'insideLabel'"
			:for="$attrs.id as string"
			:class="[
				cn(
					'peer-placeholder-shown:!opacity-1 absolute left-2 top-1 z-20 cursor-text select-none font-medium text-primary-light-1 transition-all duration-200 ease-in-out peer-placeholder-shown:top-[25%] peer-focus:top-1 peer-focus:pb-1 peer-focus:text-primary',
					props.labelClass
				),
				{ '!text-error': error }
			]"
			@click="inputRef?.focus()"
		>
			{{ props.placeholder }}
		</label>
	</div>
</template>

<style scoped>
input:focus ~ label,
input:not(:placeholder-shown) ~ label {
	top: 0.25rem;
	font-size: 10px;
	font-weight: 500;
}

input:focus + label {
	top: 0.25rem;
	font-size: 10px;
	font-weight: 500;
	color: var(--primary-color);
}
</style>
